import { getServiceByCustomerID } from '@/modules/Service/services'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AsyncSelectWithServer from '../AsyncSelectWithServer'

interface ServiceCustomerSelectProps<T extends FieldValues> {
    control: UseFormReturn<T, any, any>['control']
    name: Path<T>
    multiple?: boolean
    showLabel?: boolean
    disabled?: boolean
    label?: string
    customerID: number
    /**
     * Only offer services that can legally join an annex: none that a live contract or another
     * live annex already owns. The server enforces the same rule, so leaving this off makes the
     * picker offer options the submit will reject.
     */
    eligibleForSubcontract?: boolean
    /** The annex being edited — its own services stay selectable. */
    currentSubcontractID?: number
}

const ServiceCustomerSelect = <T extends FieldValues>({
    control,
    name,
    multiple,
    label,
    showLabel = true,
    disabled = false,
    customerID,
    eligibleForSubcontract = false,
    currentSubcontractID,
}: ServiceCustomerSelectProps<T>) => {
    const { t } = useTranslation()

    const payloadFor = (value: string) => ({
        page: 1,
        page_size: 1000,
        q: value,
        ...(eligibleForSubcontract ? { eligible_for_subcontract: true } : {}),
        ...(currentSubcontractID
            ? { current_subcontract_id: currentSubcontractID }
            : {}),
    })

    const fetchOptions = (value: string) =>
        getServiceByCustomerID({ t, payload: payloadFor(value), customerID })

    return (
        <AsyncSelectWithServer<T>
            label={showLabel ? t(label || 'employee-followers') : undefined}
            placeholder={t(label || 'employee-followers')}
            control={control}
            name={name}
            // Without this the cache key is [name, undefined, search], so switching customer (or
            // toggling the eligibility filter) reuses the previous customer's options while the
            // entry is still fresh.
            queryKey={[
                'service-customer-select',
                customerID,
                eligibleForSubcontract,
                currentSubcontractID ?? 0,
            ]}
            asyncOptions={fetchOptions}
            multiple={multiple}
            getDefaultOptions={(value) => fetchOptions(value)}
            disabled={disabled}
        />
    )
}

export default ServiceCustomerSelect
